import { useMemo, useState } from 'react'
import OutLink from '@/components/OutLink'
import { preFixImgUrl } from '@/utils'
import { LangText } from '../LangText'

const SAGAWA_TABS = [
  { id: 'all', labelKey: 'common.filter.all' },
  { id: 'narita', labelKey: 'home.sagawa.tabs.narita' },
  { id: 'haneda', labelKey: 'home.sagawa.tabs.haneda' },
  { id: 'kansai', labelKey: 'home.sagawa.tabs.kansai' },
] as const

type SagawaTab = (typeof SAGAWA_TABS)[number]['id']

type SagawaPoint = {
  id: string
  airport: Exclude<SagawaTab, 'all'>
  titleKey: string
  addressKey: string
  image: string
  url: string
}

const SAGAWA_POINTS: SagawaPoint[] = [
  {
    id: 'SOF0000000023', // 成田机场T1南
    airport: 'narita',
    titleKey: 'home.sagawa.points.naritaT1South.title',
    addressKey: 'home.sagawa.points.naritaT1South.address',
    image: preFixImgUrl('6759a36bf5e3658d44fe6f6fc70da1d9.png'),
    url: 'https://m.ctrip.com/webapp/you/gsshopping/shops/2/0.html?seo=0&ishidenavbar=yes&poiId=154106750&share=1&bid=12&pid=9&cid=2&s_guid=E805577E-5A5D-433D-9F2F-0FD78B7D0983&csharecid=12001093290328287139'
  },
  {
    id: 'SOF0000000022', // 成田机场T1北
    airport: 'narita',
    titleKey: 'home.sagawa.points.naritaT1North.title',
    addressKey: 'home.sagawa.points.naritaT1North.address',
    image: preFixImgUrl('bbed982f32c6c7ef0f0342da4e5da360.png'),
    url: 'https://m.ctrip.com/webapp/you/gsshopping/shops/2/0.html?seo=0&ishidenavbar=yes&poiId=154106764&share=1&bid=12&pid=9&cid=2&s_guid=F3C45DE5-0D87-40F4-91BF-76BB3A1EE521&csharecid=12001093290328287139'
  },
  {
    id: 'SOF0000000009', // 羽田机场T3
    airport: 'haneda',
    titleKey: 'home.sagawa.points.hanedaT3.title',
    addressKey: 'home.sagawa.points.hanedaT3.address',
    image: preFixImgUrl('cc89e1d38117c288c799f7ee428029c7.png'),
    url: 'https://m.ctrip.com/webapp/you/gsshopping/shops/2/0.html?seo=0&ishidenavbar=yes&poiId=154106803&share=1&bid=12&pid=9&cid=2&s_guid=CB772AFB-F585-41C5-911E-B9FBDABD630B&csharecid=12001093290328287139'
  },
  {
    id: 'SOF0000000024', // 成田机场T2
    airport: 'narita',
    titleKey: 'home.sagawa.points.naritaT2.title',
    addressKey: 'home.sagawa.points.naritaT2.address',
    image: preFixImgUrl('aaebba02c1d1e6b57ad9808a7b7914e7.png'),
    url: 'https://m.ctrip.com/webapp/you/gsshopping/shops/2/0.html?seo=0&ishidenavbar=yes&poiId=154106815&share=1&bid=12&pid=9&cid=2&s_guid=89723F26-49FB-4BBB-8C03-58A991F90978&csharecid=12001093290328287139'
  },
  {
    id: 'SOF0000000025', // 关西机场T1
    airport: 'kansai',
    titleKey: 'home.sagawa.points.kansaiT1.title',
    addressKey: 'home.sagawa.points.kansaiT1.address',
    image: preFixImgUrl('0fb45dc46dc4c5ecdc03b8d116c8d57a.png'),
    url: 'https://m.ctrip.com/webapp/you/gsshopping/shops/2/0.html?seo=0&ishidenavbar=yes&poiId=154106811&share=1&bid=12&pid=9&cid=2&s_guid=52E09D29-F81D-4DF5-911D-53051FFB0C25&csharecid=12001093290328287139'
  },
]

const SagawaCard = ({ point }: { point: SagawaPoint }) => (
  <div className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-[#D8D8D8] bg-white">
    <div className="h-[210px] shrink-0 overflow-hidden">
      <img
        src={point.image}
        alt=""
        className="object-cover w-full h-full transition-transform duration-500 ease-out group-hover:scale-110"
        draggable={false}
      />
    </div>
    <div className="flex flex-1 flex-col px-[12px] pb-[15px] pt-[12px]">
      <LangText
        lngKey={point.titleKey}
        className="block text-[20px] font-bold leading-[28px] text-[#000]"
      />
      <LangText
        lngKey={point.addressKey}
        className="mt-[4px] block truncate text-[12px] leading-[18px] text-[#979797]"
      />
      <div className="mt-auto pt-[18px]">
        <OutLink qrUrl={point.url}>
          <button
            type="button"
            className="h-[30px] w-full cursor-pointer rounded-full bg-primary text-[12px] text-white transition-opacity hover:opacity-90"
          >
            <LangText lngKey="common.action.bookDelivery" />
          </button>
        </OutLink>
      </div>
    </div>
  </div>
)

type SagawaDeliveryPointsProps = {
  className?: string
}

export const SagawaDeliveryPoints = ({ className = '' }: SagawaDeliveryPointsProps) => {
  const [activeTab, setActiveTab] = useState<SagawaTab>('all')

  const points = useMemo(
    () =>
      activeTab === 'all'
        ? SAGAWA_POINTS
        : SAGAWA_POINTS.filter((point) => point.airport === activeTab),
    [activeTab],
  )

  return (
    <div className={className}>
      <div className="flex items-center gap-[12px]">
        {SAGAWA_TABS.map((tab) => {
          const isActive = activeTab === tab.id

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`h-[40px] min-w-[100px] rounded-[8px] px-[18px] text-[16px] font-medium transition-colors ${
                isActive
                  ? 'border border-primary bg-secondary text-primary'
                  : 'border border-transparent bg-[#F2F2F2] text-[#979797]'
              }`}
            >
              <LangText lngKey={tab.labelKey} />
            </button>
          )
        })}
      </div>
      <div className="mt-[24px] grid grid-cols-5 gap-[16px]">
        {points.map((point) => (
          <SagawaCard key={point.id} point={point} />
        ))}
      </div>
    </div>
  )
}
